import { Tabs, TabsList, TabsTrigger } from '@sea-snag/ui';
import { createFileRoute, Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { z } from 'zod';

import { RequireOwnerOrAdmin } from '@/components/require-owner-or-admin';

import { AnalyticsFilters } from '../../components/analytics/analytics-filters';
import { useYear } from '../../hooks/use-year';
import { DEFAULT_OCCUPANCY_SORT, OCCUPANCY_SORTS } from '../../lib/occupancy-sort';
import { isSelectableYear, setYear } from '../../stores/year-store';

// Goals hold each boat's progress and its goals since #205 (Season Progress until 22 Oct).
const TAB_VALUES = ['occupancy', 'income', 'competitors', 'goals'] as const;
type TabValue = (typeof TAB_VALUES)[number];

// Income has its own boat search, owner and ownership filters, and ignores boatPid.
const TABS_WITHOUT_BOAT_FILTER = new Set<TabValue>(['income', 'goals']);

// Match the wider range used by Revenue/Goals while still covering the
// Analytics tabs. Deep links outside this window were previously crashing
// the Goals dropdown.
const searchSchema = z.object({
  year: z.coerce
    .number()
    .int()
    .min(2020)
    .max(new Date().getFullYear() + 5)
    .default(new Date().getFullYear()),
  boatPid: z.string().default('all'),
  // Occupancy-only filters. They live in the shared schema because that is where
  // every analytics search param lives; the other tabs simply ignore them.
  // '' means no filter — clearing the selection is the same as not filtering.
  boats: z.string().default(''),
  owner: z.string().default(''),
  sort: z.enum(OCCUPANCY_SORTS).default(DEFAULT_OCCUPANCY_SORT),
});

export const Route = createFileRoute('/$orgPid/analytics')({
  component: GuardedAnalyticsLayout,
  validateSearch: searchSchema,
});

/** Members are redirected to the dashboard; the API refuses them too. */
function GuardedAnalyticsLayout() {
  return (
    <RequireOwnerOrAdmin>
      <AnalyticsLayout />
    </RequireOwnerOrAdmin>
  );
}

function AnalyticsLayout() {
  const { orgPid } = Route.useParams();
  const { year: urlYear, boatPid } = Route.useSearch();
  const year = useYear();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const activeTab: TabValue = (() => {
    const segments = pathname.split('/').filter(Boolean);
    const last = segments[segments.length - 1];
    return (TAB_VALUES as readonly string[]).includes(last ?? '')
      ? (last as TabValue)
      : 'occupancy';
  })();

  const showBoatFilter = !TABS_WITHOUT_BOAT_FILTER.has(activeTab);

  // A shared ?year= link wins on arrival; after that the header owns the year
  // and the URL mirrors it, so links keep working without becoming a second
  // source of truth.
  const appliedUrlYear = useRef(false);
  useEffect(() => {
    if (appliedUrlYear.current) return;
    appliedUrlYear.current = true;
    if (isSelectableYear(urlYear) && urlYear !== year) setYear(urlYear);
  }, [urlYear, year]);

  useEffect(() => {
    if (!appliedUrlYear.current || urlYear === year) return;
    void navigate({ to: '.', search: (prev) => ({ ...prev, year }), replace: true });
  }, [year, urlYear, navigate]);

  const handleBoatPidChange = (next: string) => {
    void navigate({
      to: '.',
      search: (prev) => ({ ...prev, boatPid: next }),
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Analytics</h1>
          <p className="text-muted-foreground">
            Pricing intelligence, revenue, and fleet performance insights
          </p>
        </div>
        <AnalyticsFilters
          boatPid={boatPid}
          onBoatPidChange={handleBoatPidChange}
          showBoatFilter={showBoatFilter}
        />
      </div>

      <Tabs value={activeTab}>
        <TabsList>
          <TabsTrigger value="occupancy" asChild>
            <Link to="/$orgPid/analytics/occupancy" params={{ orgPid }} search={(prev) => prev}>
              Occupancy
            </Link>
          </TabsTrigger>
          <TabsTrigger value="income" asChild>
            <Link to="/$orgPid/analytics/income" params={{ orgPid }} search={(prev) => prev}>
              Income
            </Link>
          </TabsTrigger>
          <TabsTrigger value="competitors" asChild>
            <Link to="/$orgPid/analytics/competitors" params={{ orgPid }} search={(prev) => prev}>
              Competitors
            </Link>
          </TabsTrigger>
          <TabsTrigger value="goals" asChild>
            <Link to="/$orgPid/analytics/goals" params={{ orgPid }} search={(prev) => prev}>
              Goals
            </Link>
          </TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="mt-6">
        <Outlet />
      </div>
    </div>
  );
}
